import { BLOCK_MATH_PATTERN, INLINE_MATH_PATTERN } from './math.util';

const MATH_REGEX = new RegExp(`${BLOCK_MATH_PATTERN}|${INLINE_MATH_PATTERN}`, 'g');

/**
 * Escapes tildes inside math expressions to prevent markdown strikethrough interpretation.
 * The tildes are converted to HTML entity &#126; which is restored by the math processor.
 */
export function escapeTildesInMath(markdown: string): string {
  return markdown.replace(MATH_REGEX, (match, blockMath?: string, inlineMath?: string) => {
    const content = (blockMath ?? inlineMath ?? '').replace(/~/g, '&#126;');
    return blockMath !== undefined ? `$$${content}$$` : `$${content}$`;
  });
}

/**
 * Escapes backslashes inside math expressions to prevent `marked` from stripping them.
 *
 * Problem: `marked` treats `\` + ASCII punctuation as a markdown escape and strips the `\`.
 * For example, `\,` (LaTeX thin space) becomes just `,`.
 *
 * Solution: Replace `\` with HTML entity `&#92;` inside `$...$` and `$$...$$`.
 * `marked` passes HTML entities through as-is in text tokens, so the backslashes
 * are preserved. They are restored by `unescapeBackslashes` in the math processor.
 *
 * Related: FE-2296 (LaTeX and markdown conflicts)
 */
export function escapeBackslashesInMath(markdown: string): string {
  return markdown.replace(MATH_REGEX, (match, blockMath?: string, inlineMath?: string) => {
    const content = (blockMath ?? inlineMath ?? '').replace(/\\/g, '&#92;');
    return blockMath !== undefined ? `$$${content}$$` : `$${content}$`;
  });
}

/**
 * Restores escaped tildes (&#126;) back to ~ for KaTeX rendering
 */
export const unescapeTildes = (value: string): string => value.replaceAll('&#126;', '~');

/**
 * Restores escaped backslashes (&#92;) back to \ for KaTeX rendering
 */
export const unescapeBackslashes = (value: string): string => value.replaceAll('&#92;', '\\');
